import { CoreViewProps, FDate, View, classNames, getBoundingClientRect, useCustomEvent } from '@fold-dev/core'
import React, { useContext, useMemo, useRef } from 'react'
import { CalendarScheduleDay } from './calendar-schedule-day'
import { CalendarScheduleGutter } from './calendar-schedule-gutter'
import { CalendarContext } from './calendar.provider'
import { CalendarTypes } from './calendar.types'

export type CalendarScheduleProps = {
    days?: CalendarTypes.Schedule[]
    events: CalendarTypes.Event[]
} & CoreViewProps

export const CalendarSchedule = (props: CalendarScheduleProps) => {
    const { days, events = [], ...rest } = props
    const ref = useRef(null)
    const allEvents = useMemo(
        () => events.map((e) => ({ ...e, start: new Date(e.start), end: new Date(e.end) })),
        [events]
    )
    const { chunksInHour, gridSize, dragging, dimPastEvents, canAdd, canUpdate, hideGutter, setGhost, setDragging } =
        useContext(CalendarContext)
    const daysWithEvents = useMemo(() => {
        return days.map(({ date, workingHours = { start: null, end: null } }) => {
            const start = new Date(date.setHours(0, 0, 0, 0))
            const end = new Date(date.setHours(23, 59, 59, 999))

            return {
                day: date,
                workingHours,
                start,
                end,
                events: allEvents
                    .filter((event) => {
                        return (
                            (event.end > start && event.end <= end) ||
                            (event.start >= start && event.start <= end) ||
                            (event.start < start && event.end > end)
                        )
                    })
                    .map((event) => {
                        return {
                            ...event,
                            start: event.start < start ? start : event.start,
                            end: event.end > end ? end : event.end,
                        }
                    }),
            }
        })
    }, [days, allEvents])
    const className = classNames(
        {
            'f-calendar-schedule': true,
            'f-col': true,
            'is-dragging': dragging,
            'can-add': canAdd,
            'can-update': canUpdate,
            'dim-past': dimPastEvents,
        },
        [props.className]
    )

    const getMasterEvents = () => allEvents

    const handleTarget = ({ detail }) => {
        const event = detail

        const start = detail.start ? detail.start : new Date('1977-01-01 10:00')
        const end = detail.end ? detail.end : new Date('1977-01-01 12:00')

        const distance = 0
        const { height } = getBoundingClientRect(ref.current)
        const lines = gridSize * chunksInHour
        const chunk = Math.round((end.getTime() - start.getTime()) / lines)
        const percentDown = distance / height
        const line = Math.round(lines * percentDown)
        const startTimeOnMouseDown = start.getTime() + line * chunk
        const buffer = startTimeOnMouseDown - start.getTime()
        const ghost = { ...event, start, end, buffer, create: false, type: 'schedule' }

        setGhost({ ...ghost, originGhost: { ...ghost } })
        setDragging(true)
    }

    useCustomEvent('tasks-to-calendar', handleTarget)

    return (
        <View
            {...rest}
            className={className}>
            <div
                ref={ref}
                className="f-calendar-schedule__main f-row">
                {!hideGutter && <CalendarScheduleGutter />}

                {daysWithEvents.map(({ day, workingHours, start, end, events }, index) => {
                    return (
                        <CalendarScheduleDay
                            key={index}
                            day={day}
                            start={start}
                            end={end}
                            events={events}
                            getMasterEvents={getMasterEvents}
                            workingHours={workingHours}
                        />
                    )
                })}
            </div>
        </View>
    )
}
